import React, { useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Alert } from "reactstrap";

const KEY_RE = /^[A-Za-z0-9_-]+$/;
const OPTION_SOURCES = ["app_categories", "ctv_categories"];

const BASE_FIELDS = [
    "id",
    "key",
    "label",
    "nodeType",
    "required",
    "defaultValue",
    "description",
    "ui",
    "validation",
    "conditional",
];
const EXTRA_FIELDS = {
    object: ["children"],
    array: ["itemSchema"],
    polymorphicArray: ["discriminatorKey", "variants"],
    select: ["options", "optionsSource"],
};
const VARIANT_FIELDS = ["id", "discriminatorValue", "label", "itemSchema"];

const SAMPLE = [
    {
        key: "list",
        label: "List",
        nodeType: "polymorphicArray",
        discriminatorKey: "type",
        variants: [
            {
                discriminatorValue: "publications",
                label: "Publications",
                itemSchema: {
                    nodeType: "object",
                    key: "item",
                    label: "Item",
                    children: [
                        { key: "id", label: "Id", nodeType: "text", required: true },
                        { key: "publication_name", label: "Publication name", nodeType: "text" },
                        { key: "icon_url", label: "Icon URL", nodeType: "text", validation: { format: "url" } },
                        { key: "version", label: "Version", nodeType: "number" },
                    ],
                },
            },
        ],
    },
    {
        key: "settings",
        label: "Settings",
        nodeType: "object",
        children: [
            { key: "enabled", label: "Enabled", nodeType: "boolean" },
            { key: "theme", label: "Theme", nodeType: "select", options: ["light", "dark"] },
            { key: "primary_color", label: "Primary colour", nodeType: "color" },
            {
                key: "tabs",
                label: "Tabs",
                nodeType: "array",
                itemSchema: {
                    nodeType: "object",
                    key: "item",
                    label: "Item",
                    children: [{ key: "title", label: "Title", nodeType: "text" }],
                },
            },
        ],
    },
];

function newId() {
    return window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `n_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function isPlainObject(v) {
    return v != null && typeof v === "object" && !Array.isArray(v);
}

function isScalar(v) {
    const t = typeof v;
    return t === "string" || t === "number" || t === "boolean";
}

function normalizeOptions(raw, path, errors) {
    if (!Array.isArray(raw) || raw.length === 0) {
        errors.push(`${path}: select needs a non-empty "options" array, or an "optionsSource"`);
        return [];
    }
    const out = [];
    raw.forEach((o, i) => {
        if (isScalar(o)) {
            out.push({ value: o, image: "" });
            return;
        }
        if (isPlainObject(o) && isScalar(o.value)) {
            out.push({ value: o.value, image: typeof o.image === "string" ? o.image : "" });
            return;
        }
        errors.push(`${path}.options[${i}]: must be a scalar, or an object with a scalar "value"`);
    });
    return out;
}

/**
 * Turn one pasted node into a builder node, collecting every problem into `errors`.
 * Pasted ids are dropped and replaced — reusing them would collide with the live tree.
 * `keyOptional` covers itemSchema roots, which are always keyed "item".
 */
function sanitizeNode(raw, path, validTypes, errors, keyOptional = false) {
    if (!isPlainObject(raw)) {
        errors.push(`${path}: must be a JSON object`);
        return null;
    }

    const nodeType = raw.nodeType;
    if (typeof nodeType !== "string" || validTypes.indexOf(nodeType) === -1) {
        errors.push(`${path}: unknown nodeType ${JSON.stringify(nodeType)}`);
        return null;
    }

    const allowed = BASE_FIELDS.concat(EXTRA_FIELDS[nodeType] || []);
    Object.keys(raw).forEach((f) => {
        if (allowed.indexOf(f) === -1) {
            errors.push(`${path}: field "${f}" is not allowed on a ${nodeType} node`);
        }
    });

    let key = raw.key;
    if (key === undefined && keyOptional) key = "item";
    if (typeof key !== "string" || !KEY_RE.test(key)) {
        errors.push(`${path}: "key" must be letters, numbers, underscore or hyphen (got ${JSON.stringify(raw.key)})`);
        key = typeof key === "string" ? key : "";
    }

    const node = {
        id: newId(),
        key,
        label: typeof raw.label === "string" && raw.label.trim() ? raw.label : key,
        nodeType,
        required: raw.required === true,
        defaultValue: raw.defaultValue !== undefined ? raw.defaultValue : null,
        description: typeof raw.description === "string" ? raw.description : "",
        ui: isPlainObject(raw.ui) ? { ...raw.ui } : {},
        validation: isPlainObject(raw.validation) ? { ...raw.validation } : {},
        conditional: isPlainObject(raw.conditional) ? { ...raw.conditional } : {},
    };

    if (raw.required !== undefined && typeof raw.required !== "boolean") {
        errors.push(`${path}.required: must be true or false`);
    }

    const nodePath = path === "$" ? key || "?" : `${path}.${key || "?"}`;

    if (nodeType === "object") {
        node.children = sanitizeChildren(raw.children, nodePath, validTypes, errors);
    } else if (nodeType === "array") {
        if (raw.itemSchema === undefined) {
            errors.push(`${nodePath}: an array needs an "itemSchema"`);
            node.itemSchema = null;
        } else {
            node.itemSchema = sanitizeNode(raw.itemSchema, `${nodePath}[]`, validTypes, errors, true);
        }
    } else if (nodeType === "polymorphicArray") {
        const dk = raw.discriminatorKey;
        node.discriminatorKey = typeof dk === "string" && dk.trim() ? dk.trim() : "type";
        if (dk !== undefined && (typeof dk !== "string" || !dk.trim())) {
            errors.push(`${nodePath}: "discriminatorKey" must be a non-empty string`);
        }
        node.variants = sanitizeVariants(raw.variants, node.discriminatorKey, nodePath, validTypes, errors);
    } else if (nodeType === "select") {
        if (raw.optionsSource !== undefined && raw.optionsSource !== "") {
            if (OPTION_SOURCES.indexOf(raw.optionsSource) === -1) {
                errors.push(
                    `${nodePath}: unknown optionsSource ${JSON.stringify(raw.optionsSource)} (use ${OPTION_SOURCES.join(" or ")})`
                );
            }
            node.optionsSource = raw.optionsSource;
        } else {
            node.options = normalizeOptions(raw.options, nodePath, errors);
        }
    } else if (nodeType === "file" && !node.ui.accept) {
        node.ui.accept = "image/*";
    }

    return node;
}

function sanitizeChildren(raw, path, validTypes, errors) {
    if (raw === undefined) return [];
    if (!Array.isArray(raw)) {
        errors.push(`${path}: "children" must be an array`);
        return [];
    }
    const out = [];
    const seen = {};
    raw.forEach((child, i) => {
        const node = sanitizeNode(child, `${path}.children[${i}]`, validTypes, errors);
        if (!node) return;
        if (node.key && seen[node.key]) {
            errors.push(`${path}: duplicate key "${node.key}" in the pasted JSON`);
            return;
        }
        if (node.key) seen[node.key] = true;
        out.push(node);
    });
    return out;
}

function sanitizeVariants(raw, discriminatorKey, path, validTypes, errors) {
    if (!Array.isArray(raw) || raw.length === 0) {
        errors.push(`${path}: a polymorphicArray needs a "variants" array with at least one entry`);
        return [];
    }
    const out = [];
    const seen = {};
    raw.forEach((variant, i) => {
        const vp = `${path}.variants[${i}]`;
        if (!isPlainObject(variant)) {
            errors.push(`${vp}: must be a JSON object`);
            return;
        }
        Object.keys(variant).forEach((f) => {
            if (VARIANT_FIELDS.indexOf(f) === -1) {
                errors.push(`${vp}: field "${f}" is not allowed on a variant`);
            }
        });
        const dv = variant.discriminatorValue;
        if (!isScalar(dv)) {
            errors.push(`${vp}: "discriminatorValue" must be a string, number or boolean`);
            return;
        }
        if (seen[String(dv)]) {
            errors.push(`${path}: duplicate discriminatorValue "${dv}" in the pasted JSON`);
            return;
        }
        seen[String(dv)] = true;
        if (!isPlainObject(variant.itemSchema)) {
            errors.push(`${vp}: needs an "itemSchema" object`);
            return;
        }
        if (variant.itemSchema.nodeType !== "object") {
            errors.push(`${vp}.itemSchema: nodeType must be "object"`);
            return;
        }
        const itemSchema = sanitizeNode(variant.itemSchema, `${vp}.itemSchema`, validTypes, errors, true);
        if (!itemSchema) return;
        if ((itemSchema.children || []).some((c) => c.key === discriminatorKey)) {
            errors.push(
                `${vp}.itemSchema: cannot declare a child named "${discriminatorKey}" — that is the discriminator key`
            );
            return;
        }
        out.push({
            id: newId(),
            discriminatorValue: dv,
            label: typeof variant.label === "string" && variant.label.trim() ? variant.label : `Variant ${dv}`,
            itemSchema,
        });
    });
    return out;
}

function previewLines(nodes, prefix) {
    const out = [];
    const walk = (node, path) => {
        const p = path ? `${path}.${node.key}` : node.key;
        out.push(`${p}  —  ${node.nodeType}${node.required ? " (required)" : ""}`);
        if (node.nodeType === "object") {
            (node.children || []).forEach((c) => walk(c, p));
        } else if (node.nodeType === "array" && node.itemSchema) {
            (node.itemSchema.children || []).forEach((c) => walk(c, `${p}[]`));
        } else if (node.nodeType === "polymorphicArray") {
            (node.variants || []).forEach((v) => {
                const vp = `${p}[${node.discriminatorKey}=${v.discriminatorValue}]`;
                out.push(`${vp}  —  object`);
                (v.itemSchema.children || []).forEach((c) => walk(c, vp));
            });
        }
    };
    nodes.forEach((n) => walk(n, prefix));
    return out;
}

const SchemaFromJsonModal = ({ isOpen, toggle, targetNode, targetPath, validTypes, onApply }) => {
    const [text, setText] = useState("");
    const [result, setResult] = useState(null);

    const close = () => {
        setText("");
        setResult(null);
        toggle();
    };

    const onTextChange = (e) => {
        setText(e.target.value);
        setResult(null);
    };

    const downloadSample = () => {
        const blob = new Blob([JSON.stringify(SAMPLE, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "schema-nodes-sample.json";
        a.click();
        URL.revokeObjectURL(url);
    };

    const parse = () => {
        let parsed;
        try {
            parsed = JSON.parse(text);
        } catch (e) {
            setResult({ nodes: [], lines: [], errors: [`Invalid JSON — ${e.message}`] });
            return;
        }

        const incoming = Array.isArray(parsed) ? parsed : [parsed];
        if (incoming.length === 0) {
            setResult({ nodes: [], lines: [], errors: ["Nothing to add — the JSON is an empty array"] });
            return;
        }

        const errors = [];
        const existing = {};
        (targetNode.children || []).forEach((c) => {
            existing[c.key] = true;
        });

        const nodes = [];
        const seen = {};
        incoming.forEach((raw, i) => {
            const node = sanitizeNode(raw, "$", validTypes, errors, false);
            if (!node) return;
            if (!node.key) return;
            if (existing[node.key]) {
                errors.push(
                    `[${i}] "${node.key}" already exists on ${targetPath} — this can only add new fields, never change existing ones`
                );
                return;
            }
            if (seen[node.key]) {
                errors.push(`[${i}] duplicate key "${node.key}" in the pasted JSON`);
                return;
            }
            seen[node.key] = true;
            nodes.push(node);
        });

        setResult({
            nodes: errors.length ? [] : nodes,
            lines: errors.length ? [] : previewLines(nodes, targetPath === "root" ? "" : targetPath),
            errors,
        });
    };

    const apply = () => {
        onApply(result.nodes);
        close();
    };

    const canApply = result && result.errors.length === 0 && result.nodes.length > 0;

    return (
        <Modal isOpen={isOpen} toggle={close} size="lg">
            <ModalHeader toggle={close}>Create fields from JSON</ModalHeader>
            <ModalBody>
                <p className="small text-muted">
                    Paste one node object, or an array of them, to add under <code>{targetPath}</code>. This can only
                    add new fields — if a key already exists, or anything in the JSON is malformed, nothing is applied.
                </p>
                <p className="small text-muted">
                    Each node needs <code>key</code> and <code>nodeType</code>. Use <code>children</code> for an
                    object, <code>itemSchema</code> for an array, and <code>discriminatorKey</code> +{" "}
                    <code>variants</code> for a polymorphic array. Any <code>id</code> you paste is ignored and a fresh
                    one is generated.
                </p>
                <Button size="sm" color="secondary" outline className="mb-2" onClick={downloadSample}>
                    Download sample template
                </Button>
                <Input
                    type="textarea"
                    rows={12}
                    value={text}
                    onChange={onTextChange}
                    placeholder='[{ "key": "list", "nodeType": "object", "label": "List", "children": [] }]'
                    style={{ fontFamily: "monospace", fontSize: "12px" }}
                />
                {result && result.errors.length > 0 && (
                    <Alert color="danger" className="mt-3 mb-0">
                        <strong>
                            {result.errors.length} problem{result.errors.length === 1 ? "" : "s"} — nothing will be
                            added
                        </strong>
                        <ul className="mb-0 mt-2 pl-4 small">
                            {result.errors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
                {canApply && (
                    <Alert color="success" className="mt-3 mb-0">
                        <strong>
                            {result.lines.length} field{result.lines.length === 1 ? "" : "s"} will be added
                        </strong>
                        <pre
                            className="mb-0 mt-2 small"
                            style={{ maxHeight: "260px", overflow: "auto", whiteSpace: "pre-wrap" }}
                        >
                            {result.lines.join("\n")}
                        </pre>
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={close}>
                    Cancel
                </Button>
                <Button color="secondary" onClick={parse} disabled={!text.trim()}>
                    Parse
                </Button>
                <Button color="primary" onClick={apply} disabled={!canApply}>
                    Add to schema
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default SchemaFromJsonModal;
